import { AppValueTypes, TimestampType } from '@iotv/iotv-v3-types'
import config from '@iotv/config'
import { Milliseconds } from '../../constants'
import { ThingShadowStateZombie, TimeStampIndexPeriodNumber, TimeStampIndexPeriodString } from '../../types'

const debug = process.env.REACT_APP_DEBUG && false

export const nothingYet = true

export const timestampToMS = (ts: number | string | undefined) => {
  let tsms: number | undefined = undefined
  if (typeof ts === 'number') {
    tsms = ts > 9999999999 ? ts : ts * 1000
  } else if (typeof ts === 'string') {
    const tsnum = parseInt(ts)
    tsms = tsnum > 9999999999 ? tsnum : tsnum * 1000
  }
  return tsms
}

export const getLast24HourSlice = (tzOffset: number, pastStates: ThingShadowStateZombie[]) => {
  const days = 1
  const daysFromNow = -1
  const period = getTimePeriodNumber(tzOffset, daysFromNow, days, AppValueTypes.TIMESTAMP_SEC)
  const tssSlice = pastStates.filter((tss: ThingShadowStateZombie) => tss.timestamp >= period.start && tss.timestamp < period.end) as ThingShadowStateZombie[]
  return tssSlice
}

export function getTimePeriod(
  tzOffset: number,
  daysFromNow: number,
  numberOfDays: number,
  outputTimestampType: TimestampType,
  outputTimestampFormat: AppValueTypes,
  nowMs?: number,
) {
  let dayStart = nowMs ? new Date(nowMs) : new Date()
  dayStart.setUTCHours(tzOffset, 0, 0, 0)
  dayStart.setUTCDate(dayStart.getUTCDate() + daysFromNow)
  let dayEnd = new Date(dayStart).setUTCDate(dayStart.getUTCDate() + numberOfDays)

  const msTimestamp = { start: dayStart.valueOf(), end: dayEnd.valueOf() }
  const secTimestamp = { start: msTimestamp.start / 1000, end: msTimestamp.end / 1000 }
  if (outputTimestampFormat === AppValueTypes.TIMESTAMP_SEC || outputTimestampFormat === AppValueTypes.TIMESTAMP_STRING_SEC) {
    if (outputTimestampType === TimestampType.NUMBER) {
      return secTimestamp
    } else {
      return { start: secTimestamp.start.toString(), end: secTimestamp.end.toString() }
    }
  } else {
    if (outputTimestampType === TimestampType.NUMBER) {
      return msTimestamp
    } else {
      return { start: msTimestamp.start.toString(), end: msTimestamp.end.toString() }
    }
  }
}

export const getTimePeriodString = (tzOffset: number, daysFromNow: number, numberOfDays: number, outputTimestampFormat: AppValueTypes) =>
  getTimePeriod(tzOffset, daysFromNow, numberOfDays, TimestampType.STRING, outputTimestampFormat) as TimeStampIndexPeriodString

export const getTimePeriodNumber = (tzOffset: number, daysFromNow: number, numberOfDays: number, outputTimestampFormat: AppValueTypes) =>
  getTimePeriod(tzOffset, daysFromNow, numberOfDays, TimestampType.NUMBER, outputTimestampFormat) as TimeStampIndexPeriodNumber

export const getTZOffsetDate = (timestampMS: number, tzOffset: number) => {
  let dateAtTZ = new Date(timestampMS)
  dateAtTZ.setUTCHours(dateAtTZ.getUTCHours() + tzOffset)
  return dateAtTZ
}

/*
  Rollver Fns: rollover of M/D/Y *** in the TZ with the tzOffset *** between UTC timestamps at those UTC times

  Each isRollover Fn requires the tzOffset to be the negative of the local tzOffset eg NZ is UTC + 12, so the following works in
  both Lambda ( TZ 0 ) and locally in say NZ ( TZ +12 )
  const tzOffset = (new Date().getTimezoneOffset() 

  Lambda tzOffset = 0
  NZ tzOffset = -12

  Note: decision is NOT to account for daylight saving in Lambdas so a 'mignight' process would occur at 00:00 NZ time in winter and 01:00 NZ time in summer
  */

export const isHourRollover = (previousTimestampMS: number, currentTimestampMS: number, tzOffset: number) => {
  const prvDateTZ = getTZOffsetDate(previousTimestampMS, tzOffset)
  const currDateTZ = getTZOffsetDate(currentTimestampMS, tzOffset)
  // const hourDiff = prvDateTZ.getUTCHours() - currDateTZ.getUTCHours()
  // const dateDiff = prvDateTZ.getUTCDate() - currDateTZ.getUTCDate()
  // const monthDiff = prvDateTZ.getUTCMonth() - currDateTZ.getUTCMonth()
  // const yearDiff = prvDateTZ.getUTCFullYear() - currDateTZ.getUTCFullYear()
  return (
    previousTimestampMS < currentTimestampMS &&
    (prvDateTZ.getUTCHours() !== currDateTZ.getUTCHours() ||
      prvDateTZ.getUTCDate() !== currDateTZ.getUTCDate() ||
      prvDateTZ.getUTCMonth() !== currDateTZ.getUTCMonth() ||
      prvDateTZ.getUTCFullYear() !== currDateTZ.getUTCFullYear())
  )
}

export const isDayRollover = (previousTimestampMS: number, currentTimestampMS: number, tzOffset: number): boolean => {
  const prvDateTZ = getTZOffsetDate(previousTimestampMS, tzOffset)
  const currDateTZ = getTZOffsetDate(currentTimestampMS, tzOffset)
  const dateDiff = prvDateTZ.getUTCDate() - currDateTZ.getUTCDate()
  const monthDiff = prvDateTZ.getUTCMonth() - currDateTZ.getUTCMonth()
  const yearDiff = prvDateTZ.getUTCFullYear() - currDateTZ.getUTCFullYear()
  return previousTimestampMS < currentTimestampMS && ((dateDiff < 0 && monthDiff === 0 && yearDiff === 0) || (monthDiff < 0 && yearDiff === 0) || yearDiff < 0)
}

/**
 * Day rolls over when it has been 7 (or more) days, or to reset on mondays
 * @link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/getDay
 */
export const isWeekRollover = (previousTimestampMS: number, currentTimestampMS: number, tzOffset: number): boolean => {
  const msDifference = currentTimestampMS - previousTimestampMS
  if (msDifference < 0) {
    return false
  }
  const dayDifference = Math.floor(msDifference / Milliseconds.DAY)

  // roll over starts only on mondays (?)
  const prvDateTZ = getTZOffsetDate(previousTimestampMS, tzOffset)
  const currDateTZ = getTZOffsetDate(currentTimestampMS, tzOffset)
  const dateDiff = prvDateTZ.getUTCDate() - currDateTZ.getUTCDate()

  const dayOfWeek = currDateTZ.getDay()

  const isMonday = dayOfWeek === 1

  return dayDifference >= 7 || (isMonday && dateDiff > 0) // might not even need dayDifference
}

export const isMonthRollover = (previousTimestampMS: number, currentTimestampMS: number, tzOffset: number): boolean => {
  const prvDateTZ = getTZOffsetDate(previousTimestampMS, tzOffset)
  const currDateTZ = getTZOffsetDate(currentTimestampMS, tzOffset)
  const monthDiff = prvDateTZ.getUTCMonth() - currDateTZ.getUTCMonth()
  const yearDiff = prvDateTZ.getUTCFullYear() - currDateTZ.getUTCFullYear()
  return previousTimestampMS < currentTimestampMS && ((monthDiff < 0 && yearDiff === 0) || yearDiff < 0)
}

export const isYearRollover = (previousTimestampMS: number, currentTimestampMS: number, tzOffset: number): boolean => {
  const prvDateTZ = getTZOffsetDate(previousTimestampMS, tzOffset)
  const currDateTZ = getTZOffsetDate(currentTimestampMS, tzOffset)
  const yearDiff = prvDateTZ.getUTCFullYear() - currDateTZ.getUTCFullYear()
  return previousTimestampMS < currentTimestampMS && yearDiff < 0
}

/** Gets the timezome offset relative to UTC
 * Negated because getTimezoneOffset() returns the offset from local time To UTC
 * e.g NZT gives -12 (or -13) because UTC is 12 (or 13) hours behind
 * But we usually want the offset from UTC e.g. NZT is UTC+12 (or 13) so give us +12 (or 13)
 * @returns Timezone offset from UTC in Milliseconds
 */
export const getTimezoneOffsetFromUTC = () => {
  return new Date().getTimezoneOffset() * -Milliseconds.MINUTE
}

export const logHumanReadableDate = (timestampMs: number) => {
  debug && console.log(getHumanReadableDate(timestampMs))
}

export const getHumanReadableDate = (timestampMs: number) => {
  const date = new Date(timestampMs)
  return `${date.getDay()} : ${date.getDate()} : ${date.getMonth()} : ${date.getFullYear()}  `
}

export const getNiceDate = (timestamp: number | string, format: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'short' }) => {
  let timestampNum = typeof timestamp === 'string' ? parseInt(timestamp) : timestamp
  let timestampMsSecs: number = timestampNum < 9999999999 ? timestampNum * 1000 : timestampNum
  const datetime = new Date(timestampMsSecs)
  const nowString = new Date().toLocaleDateString(config.locale, format)
  const thenStrting = datetime.toLocaleDateString(config.locale, format)
  const isToday = nowString === thenStrting

  const dateSt = format.weekday !== 'short' && isToday ? 'Today' : thenStrting
  const timeSt = datetime.toLocaleTimeString(config.locale, { hour: 'numeric', minute: 'numeric' })
  return `${dateSt} ${timeSt}`.replace(',', '')
}

export const getGoogleDate = (timestamp: number | string) => {
  let timestampNum = typeof timestamp === 'string' ? parseInt(timestamp) : timestamp
  const date = new Date(timestampNum)
  const localTime = `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`
  return localTime
}

export const getNiceDates = (timestamps: (number | string)[]) => {
  return timestamps.map((ts) => getNiceDate(ts))
}

export const getNiceTime = (timestamp: number | string, format: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'short' }) => {
  let timestampNum = typeof timestamp === 'string' ? parseInt(timestamp) : timestamp
  let timestampMsSecs: number = timestampNum < 9999999999 ? timestampNum * 1000 : timestampNum
  const datetime = new Date(timestampMsSecs)
  const nowString = new Date().toLocaleDateString(config.locale, format)
  const thenStrting = datetime.toLocaleDateString(config.locale, format)
  const isToday = nowString === thenStrting

  const dateSt = format.weekday !== 'short' && isToday ? 'Today' : thenStrting
  const timeSt = datetime.toLocaleTimeString(config.locale, { hour: 'numeric', minute: 'numeric', second: 'numeric' })
  return `${dateSt} ${timeSt}`.replace(',', '')
}

export const getNiceDateOnly = (timestamp: number | string, format: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'short' }) => {
  let timestampNum = typeof timestamp === 'string' ? parseInt(timestamp) : timestamp
  let timestampMsSecs: number = timestampNum < 9999999999 ? timestampNum * 1000 : timestampNum
  const datetime = new Date(timestampMsSecs)
  const nowString = new Date().toLocaleDateString(config.locale, format)
  const thenStrting = datetime.toLocaleDateString(config.locale, format)
  const isToday = nowString === thenStrting
  const dateSt = format.weekday !== 'short' && isToday ? 'Today' : thenStrting

  return dateSt
}

export const getNiceTimeOnly = (timestamp: number | string, format: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: 'numeric' }) => {
  let timestampNum = typeof timestamp === 'string' ? parseInt(timestamp) : timestamp
  let timestampMsSecs: number = timestampNum < 9999999999 ? timestampNum * 1000 : timestampNum
  const datetime = new Date(timestampMsSecs)
  return `${datetime.toLocaleTimeString(config.locale, format)}`
}

/** gets epoch time in seconds based on number of days from now */
export const getTTLSeconds = (daysFromNow: number) => {
  const now = new Date()
  const ttl = Math.ceil(now.setDate(now.getDate() + daysFromNow) / 1000)
  return ttl
}

export const getHumanReadableTimeSince = (thenSeconds: number, nowishSeconds: number) => {
  thenSeconds = Math.round(thenSeconds)
  nowishSeconds = Math.round(nowishSeconds)

  const secondsDifference = Math.round(nowishSeconds - thenSeconds)
  let lazyHumanTimeDifferenceStr = `${secondsDifference} seconds`
  const minutesDiff = Math.round(secondsDifference / 60)
  const hourDiff = Math.round(minutesDiff / 60)
  const dayDiff = Math.round(hourDiff / 24)

  if (minutesDiff >= 1) {
    if (hourDiff >= 1) {
      if (dayDiff >= 1) {
        lazyHumanTimeDifferenceStr = `${dayDiff} days`
      } else {
        lazyHumanTimeDifferenceStr = `${hourDiff} hours`
      }
    } else {
      lazyHumanTimeDifferenceStr = `${minutesDiff} minutes`
    }
  }
  debug && console.log({ secondsDifference, minutesDiff, hourDiff, dayDiff, awolSinceMessage: lazyHumanTimeDifferenceStr })

  return lazyHumanTimeDifferenceStr
}

export const getHumanReadableMinSecs = (durationMs: number) => {
  const mins = Math.floor(durationMs / Milliseconds.MINUTE)
  const secs = Math.round((durationMs % Milliseconds.MINUTE) / Milliseconds.SECOND)

  return `${mins} min ${secs} sec`
}

export const toDatePickerFormat = (timestampMs: number): string => {
  let s: string = ''
  try {
    const iso = new Date(timestampMs).toISOString()
    const firstSep = iso.indexOf(':')
    const cutIdx = firstSep + 3
    s = iso.substring(0, cutIdx)
  } catch (e: any) {
    s = e.message
  }

  return s
}

export const toDatePickerFormat2 = (timestampMs: number): string => {
  let s: string = ''
  try {
    const iso = new Date(timestampMs).toISOString()
    s = iso.split('T')[0]
  } catch (e: any) {
    s = e.message
  }

  return s
}

// WARNING AXJ origin
export const getStartAndEndTimestamps = (previousTimestampMS: number, currentTimestampMS: number, tzOffset: number, yearlyOrMonthly: number) => {
  //yearlyOrMonthly-monthly=0,yearly=1
  const prvDateTZ = getTZOffsetDate(previousTimestampMS, tzOffset)
  const currDateTZ = getTZOffsetDate(currentTimestampMS, tzOffset)
  if ((yearlyOrMonthly = 0)) {
    const firstDay = new Date(prvDateTZ.getUTCFullYear(), prvDateTZ.getUTCMonth(), 1)
    const msTimestamp = { start: firstDay.valueOf(), end: prvDateTZ.valueOf() }
    return msTimestamp
  } else if (yearlyOrMonthly == 1) {
    const firstDay = new Date(prvDateTZ.getUTCFullYear(), 0, 1)
    const msTimestamp = { start: firstDay.valueOf(), end: prvDateTZ.valueOf() }
    return msTimestamp
  }
}
